import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { task } from 'ember-concurrency'
import type { IntlService } from 'ember-intl'
import type { Log } from '@blakeelearning/log'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import { on } from '@ember/modifier'
import { eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import pick from 'teacher-ui-client/helpers/pick'
import InputText from 'teacher-ui-client/components/base/input-text'
import TextArea from 'teacher-ui-client/components/base/text-area'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import eventValue from 'teacher-ui-client/helpers/event-value'
import UiButtonLinkTo from 'teacher-ui-client/components/ui-button/link-to'
import UiButton from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import FidgetLoadingIndicator from '@blakeelearning/fidget/components/fidget/loading-indicator'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import { CAPACITY_UNLIMITED } from 'teacher-ui-client/models/school-subscription'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'

type SubmitFormTaskResult = { ok: true } | { ok: false; response: Response }

type OperationType = 'Renew' | 'Upgrade'

interface Signature {
  Args: {
    softCapacity?: string // potentially "unlimited"
    partnerName: string
    partnerPhone: string
    partnerEmail: string
  }
}

interface FormParam {
  instructions: string
  subscription_mod: OperationType
  number_of_students?: string
}

export class SubscriptionFormWriting extends Component<Signature> {
  @service log!: Log

  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  @service authToken!: AuthToken

  @tracked comments = ''

  @tracked operationType: OperationType = 'Renew'

  @tracked totalNumberOfStudents?: string

  get totalNumberOfStudentsOrCapacity(): string | undefined {
    return this.totalNumberOfStudents ?? this.args.softCapacity ?? '1000' // a default value if they have no school sub
  }

  @action
  setTotalNumberOfStudents(value: string) {
    this.totalNumberOfStudents = value
  }

  @action
  setComments(comments: string) {
    this.comments = comments
  }

  @action
  setOperationType(operationType: OperationType) {
    this.operationType = operationType
  }

  @action
  resetForm() {
    this.comments = ''
    this.operationType = 'Renew'
    this.totalNumberOfStudents = undefined
  }

  get subscriptionLabel(): string {
    return this.intl.t('subscriptions.writing')
  }

  get isFormDisabled() {
    return this.isSubmitting
  }

  get isSubmitting() {
    return this.submitFormTask.isRunning
  }

  get formData(): FormParam {
    return {
      instructions: this.comments,
      subscription_mod: this.operationType,
      number_of_students: this.totalNumberOfStudentsOrCapacity,
    }
  }

  @action
  async submitForm(event: Event) {
    event.preventDefault()
    const result = await this.submitFormTask.perform(this.formData)
    if (result.ok) {
      const successMessage = this.intl.t('components.subscriptionForm.success')
      this.flashQueue.addSuccess({ subtitle: successMessage })
      this.resetForm()
    } else {
      const failureMessage = this.intl.t('components.subscriptionForm.error')
      this.flashQueue.addFail({ subtitle: failureMessage })
      this.log.error('renew/update school subscription form submission failed', result.response)
    }
  }

  submitFormTask = task(async (params: FormParam): Promise<SubmitFormTaskResult> => {
    const formData = new FormData()
    for (const [name, value] of Object.entries(params)) {
      if (value) {
        formData.append(name, String(value))
      }
    }
    const url = join(config.readingEggsBaseUrl, '/wl/school/subscription').href
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        accept: 'application/json',
        authorization: this.authToken.token ?? '',
      },
      body: formData,
    })
    if (response.ok) {
      return { ok: true }
    }
    return { ok: false, response }
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    <form {{on "submit" this.submitForm}}>
      <h4 class="my-3">
        {{t "components.subscriptionForm.title"}}
      </h4>
      <div class="border-dusty-black-100 border p-4">
        <strong class="block font-bold">
          {{t "components.subscriptionForm.renewOrUpgrade.completeThisSectionToRenewOrUpgrade"}}
        </strong>
        <label class="my-1 block font-bold">
          <span>{{t "components.subscriptionForm.renewOrUpgrade.selectLabel"}}</span>
          <BaseSelect
            name="subscription_mod"
            @themeKey={{themeKey "writing"}}
            {{on "input" (pick "target.value" this.setOperationType)}}
          >
            <option value="Renew" selected={{eq "Renew" this.operationType}}>
              {{t "components.subscriptionForm.renewOrUpgrade.renew"}}
            </option>
            <option value="Upgrade" selected={{eq "Upgrade" this.operationType}}>
              {{t "components.subscriptionForm.renewOrUpgrade.upgrade"}}
            </option>
          </BaseSelect>
        </label>
        <label class="my-6 block font-normal">
          <span>{{t "components.subscriptionForm.renew.totalNumberOfStudents"}}</span>
          {{#if (eq this.totalNumberOfStudentsOrCapacity CAPACITY_UNLIMITED)}}
            <strong class="font-bold">{{t "unlimited"}}</strong>
          {{else}}
            <InputText
              @themeKey={{themeKey "writing"}}
              class="w-32 font-bold"
              type="number"
              name="number_of_students"
              min="1"
              value={{this.totalNumberOfStudentsOrCapacity}}
              {{on "change" (pick "target.value" this.setTotalNumberOfStudents)}}
            />
          {{/if}}
        </label>
        <p class="my-4">
          {{t
            "components.subscriptionForm.contactCustomerService"
            phone=@partnerPhone
            email=@partnerEmail
            htmlSafe=true
          }}
        </p>
        <label class="my-6 block font-normal">
          <span class="font-bold">
            {{t "components.subscriptionForm.renewOrUpgrade.instructions" subscription=this.subscriptionLabel}}
          </span>
          <TextArea
            class="mt-2 w-full"
            rows="7"
            @value={{this.comments}}
            @themeKey={{themeKey "writing"}}
            {{on "input" (eventValue this.setComments)}}
          />
        </label>
        <div>
          <UiButtonLinkTo @appearance={{ButtonAppearance.muted}} @route="subscription-type.home" class="w-24">
            {{t "cancel"}}
          </UiButtonLinkTo>
          <UiButton type="submit" @appearance={{ButtonAppearance.regular}}>
            {{t "components.subscriptionForm.renewOrUpgrade.submit"}}
          </UiButton>
          <FidgetLoadingIndicator @show={{this.isSubmitting}} @centered={{true}} @overlay={{true}} />
        </div>
        <div class="mt-3 text-sm">
          {{t "components.subscriptionForm.submitEffect" subscription=this.subscriptionLabel}}
        </div>
      </div>
    </form>
  </template>
}
